import { useState, useEffect, useRef } from 'react';
import {
  useReadContracts,
  useWriteContract,
  useWaitForTransactionReceipt,
  useChainId,
  useAccount,
} from 'wagmi';
import { formatUnits, isAddress } from 'viem';
import { toast } from 'sonner';
import {
  Lock,
  ExternalLink,
  Copy,
  Check,
  AlertTriangle,
  Wallet,
  Settings2,
  Send,
  RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Badge } from '@/shared/ui/badge';
import { Skeleton } from '@/shared/ui/skeleton';
import { contracts, galactica, galacticaCassiopeia } from '@/shared/config';
import { votingEscrowAbi } from '@/shared/config/abis';

function CopyAddress({ address }: { address: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(address);
    setCopied(true);
    toast.success('Address copied');
    setTimeout(() => setCopied(false), 2000);
  };

  const truncate = (addr: string) => `${addr.slice(0, 6)}...${addr.slice(-4)}`;

  return (
    <div className="flex items-center gap-2">
      <code className="text-sm font-mono">{truncate(address)}</code>
      <button onClick={copy} className="text-[#807f7e] hover:text-[#2b2b2b]">
        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );
}

function StatRow({ label, value, isLoading }: { label: string; value: React.ReactNode; isLoading?: boolean }) {
  return (
    <div className="flex justify-between py-3 border-b border-gray-100 last:border-0">
      <span className="text-[#807f7e]">{label}</span>
      {isLoading ? <Skeleton className="h-5 w-32" /> : <span className="font-medium">{value}</span>}
    </div>
  );
}

interface AdminActionProps {
  title: string;
  description: string;
  buttonText: string;
  onAction: () => void;
  isPending: boolean;
  variant?: 'default' | 'destructive';
  inputPlaceholder?: string;
  inputValue?: string;
  onInputChange?: (value: string) => void;
  disabled?: boolean;
}

function AdminAction({
  title,
  description,
  buttonText,
  onAction,
  isPending,
  variant = 'default',
  inputPlaceholder,
  inputValue,
  onInputChange,
  disabled,
}: AdminActionProps) {
  return (
    <div className="p-4 border rounded-lg space-y-3">
      <div>
        <h4 className="font-medium">{title}</h4>
        <p className="text-sm text-[#807f7e]">{description}</p>
      </div>
      {inputPlaceholder && onInputChange && (
        <Input
          placeholder={inputPlaceholder}
          value={inputValue}
          onChange={(e) => onInputChange(e.target.value)}
          className="font-mono text-sm"
        />
      )}
      <Button
        onClick={onAction}
        disabled={isPending || disabled}
        variant={variant === 'destructive' ? 'destructive' : 'default'}
        size="sm"
      >
        {isPending ? (
          <>
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            Processing...
          </>
        ) : (
          buttonText
        )}
      </Button>
    </div>
  );
}

export function StakingPage() {
  const chainId = useChainId();
  const { address: walletAddress, isConnected } = useAccount();
  const currentChain = chainId === galactica.id ? galactica : galacticaCassiopeia;

  const votingEscrowAddress = contracts.votingEscrow[chainId as keyof typeof contracts.votingEscrow];

  // Form states
  const [newPenaltyRecipient, setNewPenaltyRecipient] = useState('');
  const [newOwner, setNewOwner] = useState('');
  const [newWGNET, setNewWGNET] = useState('');

  // Read contract data (auto-refresh every 15 seconds)
  const { data, isLoading, refetch } = useReadContracts({
    contracts: [
      { address: votingEscrowAddress, abi: votingEscrowAbi, functionName: 'totalSupply' },
      { address: votingEscrowAddress, abi: votingEscrowAbi, functionName: 'supply' },
      { address: votingEscrowAddress, abi: votingEscrowAbi, functionName: 'MAXTIME' },
      { address: votingEscrowAddress, abi: votingEscrowAbi, functionName: 'owner' },
      { address: votingEscrowAddress, abi: votingEscrowAbi, functionName: 'pendingOwner' },
      { address: votingEscrowAddress, abi: votingEscrowAbi, functionName: 'penaltyRecipient' },
      { address: votingEscrowAddress, abi: votingEscrowAbi, functionName: 'penaltyAccumulated' },
      { address: votingEscrowAddress, abi: votingEscrowAbi, functionName: 'maxPenalty' },
      { address: votingEscrowAddress, abi: votingEscrowAbi, functionName: 'wGNET' },
    ],
    query: {
      refetchInterval: 30000,
      refetchOnWindowFocus: true,
      placeholderData: (prev) => prev,
      staleTime: 30000,
      gcTime: 5 * 60 * 1000,
      retry: 3,
      retryDelay: 1000,
    },
  });

  const [
    totalSupply,
    lockedSupply,
    maxTime,
    owner,
    pendingOwner,
    penaltyRecipient,
    penaltyAccumulated,
    maxPenalty,
    wGNET,
  ] = data || [];

  // Write contract hooks
  const { writeContract, isPending: isWritePending, data: txHash } = useWriteContract();
  const { isLoading: isTxLoading, isSuccess: isTxSuccess } = useWaitForTransactionReceipt({
    hash: txHash,
  });

  // Track which tx we've already handled
  const handledTxRef = useRef<string | null>(null);

  useEffect(() => {
    if (isTxSuccess && txHash && handledTxRef.current !== txHash) {
      handledTxRef.current = txHash;
      toast.success('Transaction confirmed');
      refetch();
    }
  }, [isTxSuccess, txHash, refetch]);

  const isPending = isWritePending || isTxLoading;

  // Check if current wallet is owner
  const isOwner = walletAddress && owner?.result && walletAddress.toLowerCase() === (owner.result as string).toLowerCase();
  const isPendingOwnerWallet = walletAddress && pendingOwner?.result && walletAddress.toLowerCase() === (pendingOwner.result as string).toLowerCase();

  // Admin actions
  const handleUpdatePenaltyRecipient = () => {
    if (!isAddress(newPenaltyRecipient)) {
      toast.error('Invalid address');
      return;
    }
    writeContract({
      address: votingEscrowAddress,
      abi: votingEscrowAbi,
      functionName: 'updatePenaltyRecipient',
      args: [newPenaltyRecipient as `0x${string}`],
    });
  };

  const handleCollectPenalty = () => {
    writeContract({
      address: votingEscrowAddress,
      abi: votingEscrowAbi,
      functionName: 'collectPenalty',
    });
  };

  const handleSetWGNET = () => {
    if (!isAddress(newWGNET)) {
      toast.error('Invalid address');
      return;
    }
    writeContract({
      address: votingEscrowAddress,
      abi: votingEscrowAbi,
      functionName: 'setWGNET',
      args: [newWGNET as `0x${string}`],
    });
  };

  const handleTransferOwnership = () => {
    if (!isAddress(newOwner)) {
      toast.error('Invalid address');
      return;
    }
    writeContract({
      address: votingEscrowAddress,
      abi: votingEscrowAbi,
      functionName: 'transferOwnership',
      args: [newOwner as `0x${string}`],
    });
  };

  const handleAcceptOwnership = () => {
    writeContract({
      address: votingEscrowAddress,
      abi: votingEscrowAbi,
      functionName: 'acceptOwnership',
    });
  };

  const handleUnlock = () => {
    if (!confirm('WARNING: This action is IRREVERSIBLE. Are you sure you want to disable quit lock penalties?')) {
      return;
    }
    writeContract({
      address: votingEscrowAddress,
      abi: votingEscrowAbi,
      functionName: 'unlock',
    });
  };

  const formatLargeNumber = (value: bigint | undefined, decimals = 18) => {
    if (!value) return '0';
    const num = Number(formatUnits(value, decimals));
    return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  const explorerUrl = currentChain.blockExplorers?.default.url;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#2b2b2b]">Voting Escrow (Staking)</h1>
          <p className="text-[#807f7e]">Manage veGNET staking contract</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <a
            href={`${explorerUrl}/address/${votingEscrowAddress}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="outline">
              <ExternalLink className="w-4 h-4 mr-2" />
              Explorer
            </Button>
          </a>
        </div>
      </div>

      {/* Connection warning */}
      {!isConnected && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Wallet className="w-5 h-5 text-yellow-600" />
              <p className="text-yellow-800">Connect your wallet to use admin functions</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Contract Info */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Contract Info
            </CardTitle>
            <CardDescription>Voting Escrow contract statistics</CardDescription>
          </CardHeader>
          <CardContent>
            <StatRow
              label="Contract Address"
              value={
                <a
                  href={`${explorerUrl}/address/${votingEscrowAddress}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#f26f56] hover:underline font-mono text-sm"
                >
                  {votingEscrowAddress}
                </a>
              }
            />
            <StatRow
              label="Total veGNET Supply"
              value={`${formatLargeNumber(totalSupply?.result as bigint)} veGNET`}
              isLoading={isLoading}
            />
            <StatRow
              label="Total GNET Locked"
              value={`${formatLargeNumber(lockedSupply?.result as bigint)} GNET`}
              isLoading={isLoading}
            />
            <StatRow
              label="Max Lock Time"
              value={maxTime?.result ? `${Number(maxTime.result) / 86400} days` : '—'}
              isLoading={isLoading}
            />
            <StatRow
              label="Max Penalty"
              value={maxPenalty?.result ? `${Number(formatUnits(maxPenalty.result as bigint, 18)) * 100}%` : '—'}
              isLoading={isLoading}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings2 className="w-5 h-5" />
              Ownership & Config
            </CardTitle>
            <CardDescription>Contract ownership and configuration</CardDescription>
          </CardHeader>
          <CardContent>
            <StatRow
              label="Owner"
              value={owner?.result ? <CopyAddress address={owner.result as string} /> : '—'}
              isLoading={isLoading}
            />
            <StatRow
              label="Pending Owner"
              value={
                pendingOwner?.result && pendingOwner.result !== '0x0000000000000000000000000000000000000000' ? (
                  <CopyAddress address={pendingOwner.result as string} />
                ) : (
                  <span className="text-[#807f7e]">None</span>
                )
              }
              isLoading={isLoading}
            />
            <StatRow
              label="Penalty Recipient"
              value={penaltyRecipient?.result ? <CopyAddress address={penaltyRecipient.result as string} /> : '—'}
              isLoading={isLoading}
            />
            <StatRow
              label="Accumulated Penalties"
              value={`${formatLargeNumber(penaltyAccumulated?.result as bigint)} GNET`}
              isLoading={isLoading}
            />
            <StatRow
              label="Wrapped GNET"
              value={wGNET?.result ? <CopyAddress address={wGNET.result as string} /> : '—'}
              isLoading={isLoading}
            />
          </CardContent>
        </Card>
      </div>

      {/* Admin Actions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Send className="w-5 h-5" />
                Admin Actions
              </CardTitle>
              <CardDescription>Execute contract admin functions (owner only)</CardDescription>
            </div>
            {isOwner && (
              <Badge variant="secondary" className="bg-green-100 text-green-700">
                You are the owner
              </Badge>
            )}
            {!isOwner && isConnected && (
              <Badge variant="secondary" className="bg-yellow-100 text-yellow-700">
                Not owner
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Penalty Actions */}
          <AdminAction
            title="Update Penalty Recipient"
            description="Set the address that receives early unlock penalties"
            buttonText="Update Recipient"
            onAction={handleUpdatePenaltyRecipient}
            isPending={isPending}
            inputPlaceholder="0x..."
            inputValue={newPenaltyRecipient}
            onInputChange={setNewPenaltyRecipient}
            disabled={!isOwner}
          />

          <AdminAction
            title="Collect Penalties"
            description={`Transfer ${formatLargeNumber(penaltyAccumulated?.result as bigint)} GNET to penalty recipient`}
            buttonText="Collect Penalty"
            onAction={handleCollectPenalty}
            isPending={isPending}
            disabled={!isOwner || !penaltyAccumulated?.result}
          />

          <AdminAction
            title="Set Wrapped GNET"
            description="Update the wrapped GNET token address"
            buttonText="Set wGNET"
            onAction={handleSetWGNET}
            isPending={isPending}
            inputPlaceholder="0x..."
            inputValue={newWGNET}
            onInputChange={setNewWGNET}
            disabled={!isOwner}
          />

          <AdminAction
            title="Transfer Ownership"
            description="Transfer contract ownership to a new address (2-step)"
            buttonText="Transfer"
            onAction={handleTransferOwnership}
            isPending={isPending}
            inputPlaceholder="0x..."
            inputValue={newOwner}
            onInputChange={setNewOwner}
            disabled={!isOwner}
          />

          {isPendingOwnerWallet && (
            <AdminAction
              title="Accept Ownership"
              description="You are the pending owner. Accept to become the new owner."
              buttonText="Accept Ownership"
              onAction={handleAcceptOwnership}
              isPending={isPending}
            />
          )}

          <div className="p-4 border border-red-200 rounded-lg space-y-3 bg-red-50">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
              <div>
                <h4 className="font-medium text-red-700">Unlock (IRREVERSIBLE)</h4>
                <p className="text-sm text-red-600">
                  Permanently disables quit lock penalties. This action cannot be undone.
                </p>
              </div>
            </div>
            <Button
              onClick={handleUnlock}
              disabled={isPending || !isOwner}
              variant="destructive"
              size="sm"
            >
              {isPending ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                'Unlock Contract'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
